import type { VoidFunctionComponent } from 'react';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as _ from 'lodash';

import { type Dependency, Status } from '../../../../../../shared/dependenciesV2';
import styles from './DependencyRow.module.less';
import DependencyTitle from './DependencyTitle/DependencyTitle';
import DependencyData from './DependencyData';
import Locator from '../../../../../../shared/Locator';
import List from '../../../../generic/List';
import { getIsAnalyzing, getStatusFromDependencyState, isDependencyBlocked } from './helpers';
import StateContextPanel from './StateContextPanel';
import DetailsRow from './DetailsRow';
import type { DependencyV2Modals } from '../../hooks/useDependencyModals';
import DependencyActionDropdown from './DependencyActionDropdown';
import * as DependenciesV2Store from '../../../../../redux/dependenciesV2';
import { addAndFetchBuildWatchRevision } from '../../../../../redux/dependenciesV2/actions';
import IssueSection from './DetailsRow/IssueSection';
import DependencyRootProjects from './DependencyRootProjects';
import PackageLabelsList from '../../../../shared/packageLabels/PackageLabelsList';

type Props = {
  dependency: Dependency;
  modals: DependencyV2Modals;
};

const DependencyTableRow: VoidFunctionComponent<Props> = ({ dependency, modals }) => {
  const dispatch = useDispatch();
  const isProjectScope = useSelector(DependenciesV2Store.selectors.getIsProjectScope);
  const isReleaseGroupScope = useSelector(DependenciesV2Store.selectors.getIsReleaseGroupScope);
  const revisionInstance = useSelector(DependenciesV2Store.selectors.getBuildWatchRevision(dependency.locator));
  // Memo-ize this to prevent the Locator object from changing, triggering downstream actions
  const locator = useMemo(() => new Locator(dependency.locator), [dependency.locator]);
  const status = getStatusFromDependencyState(dependency.status);
  const isBlocked = isDependencyBlocked(dependency.issues);
  const isManual = dependency.isManual;
  const isOrgMember = useSelector(DependenciesV2Store.selectors.getIsOrgMember);

  // Load revision build data on start if the status is not analyzed
  useEffect(() => {
    if (status !== Status.Analyzed && status !== Status.Unknown) {
      // Load for all non-analyzed revisions, but only watch revisions that are in progress
      const shouldWatch = status === Status.InProgress;
      dispatch(addAndFetchBuildWatchRevision(dependency.locator, shouldWatch));
    }
    // Only include revision, because we don't want to reload data if the status changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dependency.locator]);

  // Stop watching the revision if there are no more running builds
  useEffect(() => {
    if (!revisionInstance) {
      return;
    }

    // If the dependency isn't analyzing, stop watching it. Only watch in-progress builds
    if (!getIsAnalyzing(revisionInstance?.builds ?? [])) {
      dispatch(DependenciesV2Store.actions.removeRevisionFromBuildWatch(dependency.locator));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revisionInstance]);

  // Only show the "Edit Dep" modal if being viewed by an org member.
  const dependencyTitleOnClick = isOrgMember
    ? () => modals.editDependencyModal.onOpen({ context: { revision: dependency.locator } })
    : _.noop;

  return (
    <List.RowContainer>
      <List.Row hasTightPadding>
        <div className={styles.DependencyRow}>
          <div className={styles.DependencyInfo}>
            <DependencyTitle
              title={dependency.title}
              revision={locator}
              version={dependency.version}
              depth={dependency.depth}
              layerDepth={dependency.layerDepth}
              isBlocked={isBlocked}
              isManual={isManual}
              onClick={dependencyTitleOnClick}
            />
            <DependencyData
              dependency={dependency}
              modals={modals}
              isProjectScope={isProjectScope}
            />
            {isReleaseGroupScope && (
              <DependencyRootProjects rootProjects={dependency.rootProjects} />
            )}
            {status === Status.Analyzed ? (
              <>
                <DetailsRow dependency={dependency} />
                <PackageLabelsList
                  labels={dependency.labels}
                  dependencyVersion={dependency.version}
                  dependencyLocator={locator}
                />
              </>
            ) : (
              <StateContextPanel revision={locator} status={dependency.status} modals={modals} />
            )}
          </div>
          {isOrgMember && (
            <div className={styles.DependencyRowPullRight}>
              <IssueSection dependency={dependency} />
              <DependencyActionDropdown
                dependency={dependency}
                status={status}
                modals={modals}
                isProjectScope={isProjectScope}
              />
            </div>
          )}
        </div>
      </List.Row>
    </List.RowContainer>
  );
};

export default DependencyTableRow;
